import React, { useState, useEffect } from 'react';
import { knowledgeBaseApi } from '../services/cloudApi';
import { formatErrorMessage } from '../utils/errorUtils';
import { CloseIcon, TrashIcon, ToolsIcon } from './Icons';

/**
 * Knowledge Base Manager Component
 * 
 * Simplified flow with auto-discovery:
 * 1. User enters Pinecone index name + host
 * 2. Click "Discover" to auto-fetch namespaces & metadata
 * 3. User selects which namespaces to include
 * 4. User writes tool name + description (prompt for agent)
 * 5. Save
 */

function KnowledgeBaseManager({ isOpen, onClose, onKnowledgeBaseChange }) {
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingKB, setEditingKB] = useState(null);
  const [testingKB, setTestingKB] = useState(null);
  const [testQuery, setTestQuery] = useState('');
  const [testResults, setTestResults] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  
  // Discovery state
  const [discovering, setDiscovering] = useState(false);
  const [discoveryResult, setDiscoveryResult] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    pinecone_index_name: '',
    pinecone_host: '',
    pinecone_namespaces: [],  // Selected namespaces
    embedding_model: 'text-embedding-3-small',
    embedding_dimension: 1536,
    metadata_schema: {},
    tool_name: '',
    tool_description: '',
  });

  useEffect(() => {
    if (isOpen) {
      loadKnowledgeBases();
    }
  }, [isOpen]);

  const loadKnowledgeBases = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await knowledgeBaseApi.list({ includeInactive: true });
      setKnowledgeBases(response.knowledge_bases || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDiscover = async () => {
    if (!formData.pinecone_index_name.trim()) {
      setError('Please enter an index name');
      return;
    }
    
    setDiscovering(true);
    setError(null);
    setDiscoveryResult(null);
    
    try {
      const result = await knowledgeBaseApi.introspect({
        pinecone_index_name: formData.pinecone_index_name.trim(),
        pinecone_host: formData.pinecone_host.trim() || null,
      });
      
      if (result.success) {
        setDiscoveryResult(result);
        // Auto-select all namespaces
        const allNs = result.namespaces.map(ns => ns.name === '(default)' ? '' : ns.name);
        setFormData(prev => ({
          ...prev,
          pinecone_namespaces: allNs,
          embedding_dimension: result.dimension || 1536,
          // Auto-generate tool name from index name
          tool_name: prev.tool_name || `search_${formData.pinecone_index_name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}`,
        }));
      } else {
        setError(result.error || 'Failed to connect to Pinecone');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setDiscovering(false);
    }
  };

  const handleCreateKB = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Clean up namespaces - remove empty string for "all"
    const cleanedData = {
      ...formData,
      pinecone_namespaces: formData.pinecone_namespaces.length === 0 ? null : formData.pinecone_namespaces,
    };
    
    try {
      await knowledgeBaseApi.create(cleanedData);
      setShowCreateForm(false);
      resetForm();
      loadKnowledgeBases();
      // Notify parent to refresh tools list
      if (onKnowledgeBaseChange) onKnowledgeBaseChange();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateKB = async (e) => {
    e.preventDefault();
    setError(null);
    
    const cleanedData = {
      ...formData,
      pinecone_namespaces: formData.pinecone_namespaces.length === 0 ? null : formData.pinecone_namespaces,
    };
    
    try {
      await knowledgeBaseApi.update(editingKB.id, cleanedData);
      setEditingKB(null);
      resetForm();
      loadKnowledgeBases();
      // Notify parent to refresh tools list
      if (onKnowledgeBaseChange) onKnowledgeBaseChange();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteKB = async (kbId) => {
    if (!window.confirm('Are you sure you want to delete this knowledge base?')) return;
    try {
      await knowledgeBaseApi.delete(kbId);
      loadKnowledgeBases();
      // Notify parent to refresh tools list
      if (onKnowledgeBaseChange) onKnowledgeBaseChange();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleTestKB = async () => {
    if (!testQuery.trim()) return;
    setTestLoading(true);
    setTestResults(null);
    try {
      const response = await knowledgeBaseApi.test(testingKB.id, {
        query: testQuery,
        top_k: 3,
      });
      setTestResults(response);
    } catch (err) {
      setTestResults({ success: false, error: err.message });
    } finally {
      setTestLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      pinecone_index_name: '',
      pinecone_host: '',
      pinecone_namespaces: [],
      embedding_model: 'text-embedding-3-small',
      embedding_dimension: 1536,
      metadata_schema: {},
      tool_name: '',
      tool_description: '',
    });
    setDiscoveryResult(null);
  };

  const startEdit = (kb) => {
    setEditingKB(kb);
    setFormData({
      name: kb.name,
      description: kb.description || '',
      pinecone_index_name: kb.pinecone_index_name,
      pinecone_host: kb.pinecone_host || '',
      pinecone_namespaces: kb.pinecone_namespaces || [],
      embedding_model: kb.embedding_model,
      embedding_dimension: kb.embedding_dimension,
      metadata_schema: kb.metadata_schema || {},
      tool_name: kb.tool_name,
      tool_description: kb.tool_description,
    });
    setDiscoveryResult(null);
  };

  const toggleNamespace = (ns) => {
    const nsValue = ns === '(default)' ? '' : ns;
    setFormData(prev => {
      const current = prev.pinecone_namespaces || [];
      if (current.includes(nsValue)) {
        return { ...prev, pinecone_namespaces: current.filter(n => n !== nsValue) };
      } else {
        return { ...prev, pinecone_namespaces: [...current, nsValue] };
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div className="kb-manager-overlay">
      <div className="kb-manager-modal">
        {/* Header */}
        <div className="kb-manager-header">
          <div className="kb-header-title">
            <ToolsIcon size={18} />
            <h2>Knowledge Bases</h2>
          </div>
          <button className="kb-close-btn" onClick={onClose}>
            <CloseIcon size={16} />
          </button>
        </div>

        {/* Error display */}
        {error && (
          <div className="kb-error">
            <span>{formatErrorMessage(error)}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {/* Content */}
        <div className="kb-manager-content">
          {/* Left Panel - List */}
          <div className="kb-list-panel">
            <div className="kb-list-header">
              <h3>Your Knowledge Bases</h3>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => {
                  resetForm();
                  setEditingKB(null);
                  setShowCreateForm(true);
                }}
              >
                + New
              </button>
            </div>

            {loading ? (
              <div className="kb-loading">
                <div className="kb-loading-spinner"></div>
                <span>Loading...</span>
              </div>
            ) : knowledgeBases.length === 0 ? (
              <div className="kb-empty">
                <div className="kb-empty-icon">
                  <ToolsIcon size={32} />
                </div>
                <p>No knowledge bases yet</p>
                <p className="kb-empty-hint">Connect a Pinecone index to enable custom RAG tools</p>
              </div>
            ) : (
              <div className="kb-list">
                {knowledgeBases.map((kb) => (
                  <div
                    key={kb.id}
                    className={`kb-list-item ${editingKB?.id === kb.id ? 'active' : ''} ${testingKB?.id === kb.id ? 'testing' : ''}`}
                  >
                    <div className="kb-list-item-header" onClick={() => startEdit(kb)}>
                      <div className="kb-list-item-name">{kb.name}</div>
                      <div className={`kb-status-dot ${kb.test_status || 'untested'}`} 
                           title={kb.test_status === 'success' ? 'Test passed' : kb.test_status === 'failed' ? 'Test failed' : 'Not tested'}>
                      </div>
                    </div>
                    <div className="kb-list-item-info">
                      <code>{kb.tool_name}</code>
                      <span className="kb-index">{kb.pinecone_index_name}</span>
                    </div>
                    <div className="kb-list-item-actions">
                      <button
                        className="kb-action-btn"
                        onClick={() => {
                          setTestingKB(kb);
                          setTestQuery('');
                          setTestResults(null);
                        }}
                        title="Test connection"
                      >
                        Test
                      </button>
                      <button
                        className="kb-action-btn kb-action-btn-danger"
                        onClick={() => handleDeleteKB(kb.id)}
                        title="Delete"
                      >
                        <TrashIcon size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Panel - Form or Test */}
          <div className="kb-detail-panel">
            {testingKB ? (
              <div className="kb-test-panel">
                <div className="kb-panel-header">
                  <h3>Test Connection</h3>
                  <span className="kb-panel-subtitle">{testingKB.name}</span>
                </div>
                <p className="kb-test-desc">Run a sample query to verify the connection.</p>

                <div className="input-group">
                  <label className="input-label">Test Query</label>
                  <textarea
                    className="input"
                    value={testQuery}
                    onChange={(e) => setTestQuery(e.target.value)}
                    placeholder="e.g., How do I authenticate?"
                    rows={3}
                  />
                </div>

                <div className="kb-test-actions">
                  <button
                    className="btn btn-primary"
                    onClick={handleTestKB}
                    disabled={testLoading || !testQuery.trim()}
                  >
                    {testLoading ? 'Testing...' : 'Run Test'}
                  </button>
                  <button className="btn btn-secondary" onClick={() => setTestingKB(null)}>
                    Cancel
                  </button>
                </div>

                {testResults && (
                  <div className={`kb-test-results ${testResults.success ? 'success' : 'error'}`}>
                    <div className="kb-test-results-header">
                      <span className={`kb-test-status-icon ${testResults.success ? 'success' : 'error'}`}></span>
                      <span>{testResults.success ? 'Connection Successful' : 'Connection Failed'}</span>
                      {testResults.latency_ms && <span className="kb-test-latency">{testResults.latency_ms}ms</span>}
                    </div>
                    {testResults.error && <p className="kb-test-error">{formatErrorMessage(testResults.error)}</p>}
                    {testResults.results && testResults.results.length > 0 && (
                      <div className="kb-test-results-list">
                        <div className="kb-test-results-label">Top {testResults.results.length} Results</div>
                        {testResults.results.map((result, idx) => (
                          <div key={idx} className="kb-test-result-item">
                            <div className="result-score">
                              {(result.score * 100).toFixed(1)}% match
                            </div>
                            <pre className="result-metadata">
                              {JSON.stringify(result.metadata, null, 2)}
                            </pre>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (showCreateForm || editingKB) ? (
              <form onSubmit={editingKB ? handleUpdateKB : handleCreateKB} className="kb-form">
                <div className="kb-panel-header">
                  <h3>{editingKB ? 'Edit Knowledge Base' : 'New Knowledge Base'}</h3>
                </div>

                {/* Step 1: Connect to Pinecone */}
                <div className="kb-form-section">
                  <div className="kb-section-title">
                    <span className="kb-step-number">1</span>
                    Connect to Pinecone
                  </div>
                  
                  <div className="input-group">
                    <label className="input-label">Index Name <span className="required">*</span></label>
                    <input
                      type="text"
                      className="input font-mono"
                      value={formData.pinecone_index_name}
                      onChange={(e) => setFormData({ ...formData, pinecone_index_name: e.target.value })}
                      placeholder="my-docs-index"
                      required
                    />
                  </div>

                  <div className="input-group">
                    <label className="input-label">Host URL <span className="required">*</span></label>
                    <input
                      type="text"
                      className="input font-mono"
                      value={formData.pinecone_host}
                      onChange={(e) => setFormData({ ...formData, pinecone_host: e.target.value })}
                      placeholder="https://my-index-abc123.svc.us-east1-gcp.pinecone.io"
                    />
                    <div className="input-hint">Find this in Pinecone dashboard → Connect</div>
                  </div>

                  <button
                    type="button"
                    className="btn btn-discover"
                    onClick={handleDiscover}
                    disabled={discovering || !formData.pinecone_index_name.trim()}
                  >
                    {discovering ? (
                      <>
                        <span className="btn-spinner"></span>
                        Discovering...
                      </>
                    ) : (
                      <>Discover Index</>
                    )}
                  </button>
                </div>

                {/* Discovery Results */}
                {discoveryResult && (
                  <div className="kb-discovery-result">
                    <div className="kb-discovery-stats">
                      <div className="stat">
                        <span className="stat-value">{discoveryResult.total_vectors?.toLocaleString()}</span>
                        <span className="stat-label">vectors</span>
                      </div>
                      <div className="stat">
                        <span className="stat-value">{discoveryResult.dimension}</span>
                        <span className="stat-label">dimensions</span>
                      </div>
                      <div className="stat">
                        <span className="stat-value">{discoveryResult.namespaces?.length || 0}</span>
                        <span className="stat-label">namespaces</span>
                      </div>
                    </div>

                    {/* Namespace Selection */}
                    {discoveryResult.namespaces?.length > 0 && (
                      <div className="kb-namespace-select">
                        <label className="input-label">Select Namespaces to Search</label>
                        <div className="kb-namespace-list">
                          {discoveryResult.namespaces.map((ns) => {
                            const nsValue = ns.name === '(default)' ? '' : ns.name;
                            const isSelected = (formData.pinecone_namespaces || []).includes(nsValue);
                            return (
                              <label key={ns.name} className={`kb-namespace-item ${isSelected ? 'selected' : ''}`}>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleNamespace(ns.name)}
                                />
                                <span className="ns-name">{ns.name}</span>
                                <span className="ns-count">{ns.vector_count.toLocaleString()}</span>
                              </label>
                            );
                          })}
                        </div>
                        <div className="input-hint">
                          {(formData.pinecone_namespaces || []).length === 0 
                            ? 'No namespaces selected - will search ALL' 
                            : `Searching ${formData.pinecone_namespaces.length} namespace(s)`}
                        </div>
                      </div>
                    )}

                    {/* Discovered Metadata Fields */}
                    {discoveryResult.discovered_metadata_fields?.length > 0 && (
                      <div className="kb-discovered-fields">
                        <label className="input-label">Discovered Metadata Fields</label>
                        <div className="kb-field-tags">
                          {discoveryResult.discovered_metadata_fields.map(field => (
                            <span key={field} className="kb-field-tag">{field}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 2: Configure Tool */}
                <div className="kb-form-section">
                  <div className="kb-section-title">
                    <span className="kb-step-number">2</span>
                    Configure Agent Tool
                  </div>

                  <div className="input-group">
                    <label className="input-label">Display Name <span className="required">*</span></label>
                    <input
                      type="text"
                      className="input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Product Documentation"
                      required
                    />
                  </div>

                  <div className="input-group">
                    <label className="input-label">Tool Name <span className="required">*</span></label>
                    <input
                      type="text"
                      className="input font-mono"
                      value={formData.tool_name}
                      onChange={(e) => setFormData({ ...formData, tool_name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                      placeholder="search_product_docs"
                      required
                    />
                    <div className="input-hint">Identifier for agents (snake_case)</div>
                  </div>

                  <div className="input-group">
                    <label className="input-label">Tool Prompt <span className="required">*</span></label>
                    <textarea
                      className="input"
                      value={formData.tool_description}
                      onChange={(e) => setFormData({ ...formData, tool_description: e.target.value })}
                      placeholder="Search the product documentation for API usage, tutorials, and examples. Use this when the user asks technical questions about implementation."
                      rows={4}
                      required
                    />
                    <div className="input-hint">Tell the agent WHEN and WHY to use this tool</div>
                  </div>
                </div>

                <div className="kb-form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => {
                    setShowCreateForm(false);
                    setEditingKB(null);
                    resetForm();
                  }}>
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={!formData.name || !formData.tool_name || !formData.tool_description || !formData.pinecone_index_name}
                  >
                    {editingKB ? 'Save Changes' : 'Create Knowledge Base'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="kb-empty-detail">
                <div className="kb-empty-detail-icon">
                  <ToolsIcon size={48} />
                </div>
                <h3>Connect a Pinecone Index</h3>
                <p>Create a knowledge base to give your agents access to custom documentation.</p>
                <div className="kb-help-section">
                  <h4>How it works</h4>
                  <ol>
                    <li><strong>Enter your Pinecone index details</strong> - just the name and host URL</li>
                    <li><strong>Click Discover</strong> - we'll auto-detect namespaces and metadata</li>
                    <li><strong>Select namespaces</strong> - choose which to search (or all)</li>
                    <li><strong>Write a prompt</strong> - tell agents when to use this tool</li>
                    <li><strong>Assign to agents</strong> - enable in the Agent Designer</li>
                  </ol>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default KnowledgeBaseManager;
